// ────────────────────────────────────────────────────────────────
// src/views/admin/users/employees/view/EmployeeViewPage.jsx
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { useParams }   from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  RiUserLine,
  RiLockLine,
  RiMapPinLine,
  RiImageEditLine,
  RiTimeLine,
  RiCalendarCheckLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';

import Breadcrumb from '@/components/common/Breadcrumb';
import Card       from '@/components/ui/cards/Card';
import ToastMe    from '@/components/ui/ToastMe';
import {
  /* legacy helper kept in case it’s referenced elsewhere */
  getFileUrl,
  /* signed‑URL helper (adds ?tk=<jwt>, URI‑encodes the name) */
  getFileUrlSigned,
} from '@/services/CommonService';

import {
  getByIdAdminService,
  updateAdminPhotoService,
} from '@/services/AdminServices';

import ProfileTab   from './tabs/ProfileTab';
import SecurityTab  from './tabs/SecurityTab';
import AddressTab   from './tabs/AddressTab';
import AttendedTab  from './tabs/AttendedTab';
import LeaveTab     from './tabs/LeaveTab';
import PayrollTab   from './tabs/PayrollTab';

/* ------------------------------------------------------------------ */
/* Tab metadata                                                        */
/* ------------------------------------------------------------------ */
const TAB_ITEMS = [
  { label: 'Profile',    key: 'profile',    Icon: RiUserLine          },
  { label: 'Security',   key: 'security',   Icon: RiLockLine          },
  { label: 'Address',    key: 'address',    Icon: RiMapPinLine        },
  { label: 'Attendance', key: 'attendance', Icon: RiTimeLine          },
  { label: 'Leave',      key: 'leave',      Icon: RiCalendarCheckLine },
  { label: 'Payroll',    key: 'payroll',    Icon: RiMoneyDollarCircleLine },
];

/* ================================================================== */
/* Main component                                                      */
/* ================================================================== */
export default function EmployeeViewPage({ pageTitle }) {
  const { id }   = useParams();
  const dispatch = useDispatch();

  const [emp,  setEmp]  = useState(null);
  const [load, setLoad] = useState(true);
  const [tab,  setTab]  = useState('profile');

  /* ─────────────── fetch employee once on mount ─────────────── */
  useEffect(() => {
    dispatch(getByIdAdminService(id))
      .then(r => setEmp(r.data?.data))
      .catch(e => ToastMe(e.message || 'Failed to load employee', 'error'))
      .finally(() => setLoad(false));
  }, [id, dispatch]);

  /* ─────────────── handle avatar upload ─────────────── */
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !emp?._id) return ToastMe('No employee found', 'error');

    const fd = new FormData();
    fd.append('photo', file);

    try {
      await dispatch(updateAdminPhotoService(emp._id, fd));
      ToastMe('Profile photo updated', 'success');

      // re‑fetch to get the new file_name
      const fresh = await dispatch(getByIdAdminService(emp._id));
      setEmp(fresh.data?.data);
    } catch (err) {
      ToastMe(err.message || 'Upload failed', 'error');
    }
  };

  /* ─────────────── render states ─────────────── */
  if (load) return <Card className="p-6 text-center">Loading…</Card>;
  if (!emp)  return <Card className="p-6 text-center">No employee found.</Card>;

  /* ─────────────── build signed image URL (JWT + cache‑bust) ─────────────── */
  const baseAvatar = emp.file_name
    ? getFileUrlSigned(emp.file_name, 'admins')   // folder = 'admins'
    : null;

  const avatarUrl = baseAvatar
    ? `${baseAvatar}${baseAvatar.includes('?') ? '&' : '?'}t=${Date.now()}`
    : '/images/default-avatar.png';

  /* ================================================================== */
  /* Mark‑up                                                            */
  /* ================================================================== */
  return (
    <>
      {/* breadcrumb */}
      <div className="pt-[18px]">
        <Breadcrumb pageTitle={pageTitle} />
      </div>

      {/* header */}
      <Card className="rounded-xl border border-[#f0f2f8] bg-white p-6">
        <div className="flex gap-6 items-start">
          {/* photo */}
          <div className="shrink-0">
            <div className="w-[120px] h-[120px] rounded-[6px] overflow-hidden border border-slate-200">
              <img
                src={avatarUrl}
                alt={emp.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* name + button */}
          <div className="flex flex-col">
            <h2 className="text-[28px] font-bold tracking-tight text-slate-900">
              {emp.name}
            </h2>
            <span className="mt-2 inline-block rounded bg-[#f8e8b7] px-2 py-[2px] text-xs font-semibold uppercase text-[#b38a00]">
              {emp.role_id?.name || 'Staff'}
            </span>

            <button
              onClick={() => document.getElementById('photoUpload').click()}
              className="mt-6 inline-flex items-center gap-2 h-[44px] w-[220px] rounded bg-[#0065ff] text-white hover:bg-[#0056e0]"
            >
              <RiImageEditLine className="text-[20px]" />
              Upload New Photo
            </button>

            <input
              id="photoUpload"
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </div>
        </div>
      </Card>

      {/* tabs */}
      <div className="mt-6 px-6">
        <ul className="flex space-x-6">
          {TAB_ITEMS.map(({ key, label, Icon }) => (
            <li key={key}>
              <button
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition ${
                  tab === key
                    ? 'bg-[#0f62fe] text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className="text-base" /> {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* tab content */}
      <div className="mt-6">
        {tab === 'profile'    && <ProfileTab   employee={emp}        />}
        {tab === 'security'   && <SecurityTab  employeeId={emp._id} />}
        {tab === 'address'    && <AddressTab   employeeId={emp._id} />}
        {tab === 'attendance' && <AttendedTab  employeeId={emp._id} />}
        {tab === 'leave'      && <LeaveTab     employeeId={emp._id} />}
        {tab === 'payroll'    && <PayrollTab   employeeId={emp._id} />}
      </div>
    </>
  );
}
